import React, { useState, useRef, useEffect, useCallback } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Play, Pause, Volume2, VolumeX,
    Image, Video, Music, FileText, ExternalLink, Loader2,
    GripVertical, Trash2, Crop, Type
} from 'lucide-react';

/**
 * MediaCardNodeView Component
 * 
 * A Notion-style media card with:
 * - Clean image display without overlays
 * - Floating action toolbar (delete, crop, caption)
 * - Resize handles on corners
 * - Draggable media blocks
 * - Editable caption support
 */
const MediaCardNodeView = ({ node, editor, getPos, selected, updateAttributes }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [showCropModal, setShowCropModal] = useState(false);
    const [showCaption, setShowCaption] = useState(!!node.attrs.caption);
    const [caption, setCaption] = useState(node.attrs.caption || '');
    const [mediaWidth, setMediaWidth] = useState(node.attrs.width || 80); // percentage

    const mediaRef = useRef(null);
    const containerRef = useRef(null);
    const resizeStartRef = useRef({ x: 0, width: 0 });

    // Extract attributes from node
    const {
        src,
        title,
        assetType,
        fileName,
        assetId
    } = node.attrs;

    /**
     * Check if image is already cached/loaded on mount
     */
    useEffect(() => {
        if (assetType === 'image' && src) {
            // Check if image is already in cache
            const img = new window.Image();
            img.src = src;
            if (img.complete) {
                console.log('[MediaCardNodeView] Image already cached:', src?.substring(0, 50));
                setIsLoading(false);
                setImageLoaded(true);
            }
        }
    }, [assetType, src]);

    /**
     * Handle media load success
     */
    const handleLoaded = useCallback(() => {
        console.log('[MediaCardNodeView] Image loaded successfully:', src?.substring(0, 50));
        setIsLoading(false);
        setHasError(false);
        setImageLoaded(true);
        if (mediaRef.current && (assetType === 'audio' || assetType === 'video')) {
            setDuration(mediaRef.current.duration || 0);
        }
    }, [assetType, src]);

    /**
     * Handle media load error
     */
    const handleError = useCallback(() => {
        setIsLoading(false);
        setHasError(true);
    }, []);

    /**
     * Toggle play/pause for audio/video
     */
    const togglePlay = useCallback((e) => {
        e?.stopPropagation();
        if (!mediaRef.current) return;
        if (isPlaying) {
            mediaRef.current.pause();
        } else {
            mediaRef.current.play();
        }
        setIsPlaying(!isPlaying);
    }, [isPlaying]);

    /**
     * Toggle mute
     */
    const toggleMute = useCallback((e) => {
        e?.stopPropagation();
        if (!mediaRef.current) return;
        mediaRef.current.muted = !isMuted;
        setIsMuted(!isMuted);
    }, [isMuted]);

    /**
     * Handle time update for progress bar
     */
    const handleTimeUpdate = useCallback(() => {
        if (!mediaRef.current) return;
        const currentProgress = (mediaRef.current.currentTime / mediaRef.current.duration) * 100;
        setProgress(currentProgress);
    }, []);

    /**
     * Handle progress bar click
     */
    const handleProgressClick = useCallback((e) => {
        if (!mediaRef.current) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const clickPosition = (e.clientX - rect.left) / rect.width;
        mediaRef.current.currentTime = clickPosition * mediaRef.current.duration;
    }, []);

    /**
     * Format time in MM:SS
     */
    const formatTime = (seconds) => {
        if (!seconds || isNaN(seconds)) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    /**
     * Handle delete
     */
    const handleDelete = useCallback(() => {
        const pos = getPos();
        if (pos !== undefined) {
            editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run();
        }
    }, [editor, getPos, node.nodeSize]);

    /**
     * Handle caption save
     */
    const handleCaptionSave = useCallback(() => {
        updateAttributes({ caption });
        if (caption.trim()) {
            setShowCaption(true);
        }
    }, [caption, updateAttributes]);

    /**
     * Handle resize start
     */
    const handleResizeStart = useCallback((e, direction) => {
        e.preventDefault();
        e.stopPropagation();
        setIsResizing(true);
        resizeStartRef.current = {
            x: e.clientX,
            width: mediaWidth,
            direction
        };
    }, [mediaWidth]);

    /**
     * Handle resize move
     */
    useEffect(() => {
        if (!isResizing) return;

        const handleMouseMove = (e) => {
            const deltaX = e.clientX - resizeStartRef.current.x;
            const containerWidth = containerRef.current?.parentElement?.offsetWidth || 800;
            const deltaPercent = (deltaX / containerWidth) * 100;
            let newWidth = resizeStartRef.current.width + deltaPercent;

            // Clamp between 20% and 100%
            newWidth = Math.min(Math.max(newWidth, 20), 100);
            setMediaWidth(newWidth);
        };

        const handleMouseUp = () => {
            setIsResizing(false);
            updateAttributes({ width: mediaWidth });
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing, mediaWidth, updateAttributes]);

    /**
     * Get icon based on asset type
     */
    const getTypeIcon = () => {
        switch (assetType) {
            case 'image':
                return <Image size={14} className="text-green-500" />;
            case 'video':
                return <Video size={14} className="text-red-500" />;
            case 'audio':
                return <Music size={14} className="text-purple-500" />;
            case 'document':
                return <FileText size={14} className="text-blue-500" />;
            default:
                return <FileText size={14} className="text-slate-400" />;
        }
    };

    /**
     * Render image card
     */
    const renderImageCard = () => (
        <div className="relative inline-block">
            {/* Loading state */}
            {isLoading && !imageLoaded && (
                <div className="flex items-center justify-center bg-slate-50 min-h-[200px] rounded-lg" style={{ width: `${mediaWidth}%` }}>
                    <Loader2 size={24} className="animate-spin text-indigo-500" />
                </div>
            )}

            {/* Error state */}
            {hasError && (
                <div className="flex flex-col items-center justify-center bg-slate-50 min-h-[200px] rounded-lg" style={{ width: `${mediaWidth}%` }}>
                    <Image size={32} className="text-slate-300 mb-2" />
                    <p className="text-sm text-slate-400">Failed to load image</p>
                </div>
            )}

            {/* Image - no overlay, clean display */}
            {!hasError && (
                <img
                    ref={mediaRef}
                    src={src}
                    alt={caption || title || fileName || 'Image'}
                    className="h-auto object-cover rounded-lg"
                    style={{ maxWidth: `${mediaWidth}%`, display: isLoading && !imageLoaded ? 'none' : 'block' }}
                    onLoad={handleLoaded}
                    onError={handleError}
                    draggable={false}
                />
            )}
        </div>
    );

    /**
     * Render video card
     */
    const renderVideoCard = () => (
        <div className="relative inline-block rounded-lg overflow-hidden bg-slate-900" style={{ maxWidth: `${mediaWidth}%` }}>
            {/* Loading state */}
            {isLoading && (
                <div className="flex items-center justify-center bg-slate-800 min-h-[200px]">
                    <Loader2 size={24} className="animate-spin text-indigo-500" />
                </div>
            )}

            {/* Video element */}
            <video
                ref={mediaRef}
                src={src}
                className="h-auto w-full rounded-lg"
                onLoadedMetadata={handleLoaded}
                onError={handleError}
                onTimeUpdate={handleTimeUpdate}
                onEnded={() => setIsPlaying(false)}
                muted={isMuted}
                playsInline
                draggable={false}
            />

            {/* Play button overlay */}
            <AnimatePresence>
                {!isPlaying && !hasError && !isLoading && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer z-10"
                        onClick={togglePlay}
                    >
                        <div className="p-3 bg-white/90 rounded-full shadow-lg">
                            <Play size={24} className="text-slate-700 ml-0.5" />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Video controls */}
            {!isLoading && !hasError && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 z-20">
                    {/* Progress bar */}
                    <div
                        className="w-full h-1 bg-white/30 rounded-full mb-1.5 cursor-pointer"
                        onClick={handleProgressClick}
                    >
                        <div
                            className="h-full bg-indigo-500 rounded-full transition-all"
                            style={{ width: `${progress}%` }}
                        />
                    </div>

                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                            <button
                                onClick={togglePlay}
                                className="p-1 hover:bg-white/20 rounded transition-colors"
                            >
                                {isPlaying ? (
                                    <Pause size={16} className="text-white" />
                                ) : (
                                    <Play size={16} className="text-white" />
                                )}
                            </button>
                            <button
                                onClick={toggleMute}
                                className="p-1 hover:bg-white/20 rounded transition-colors"
                            >
                                {isMuted ? (
                                    <VolumeX size={16} className="text-white" />
                                ) : (
                                    <Volume2 size={16} className="text-white" />
                                )}
                            </button>
                            <span className="text-[10px] text-white/80">
                                {formatTime(mediaRef.current?.currentTime || 0)} / {formatTime(duration)}
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    /**
     * Render audio card
     */
    const renderAudioCard = () => (
        <div className="rounded-lg bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200/60 p-3" style={{ maxWidth: `${mediaWidth}%` }}>
            {/* Loading state */}
            {isLoading && (
                <div className="flex items-center justify-center py-4">
                    <Loader2 size={20} className="animate-spin text-purple-500" />
                </div>
            )}

            {/* Audio element (hidden) */}
            <audio
                ref={mediaRef}
                src={src}
                onLoadedMetadata={handleLoaded}
                onError={handleError}
                onTimeUpdate={handleTimeUpdate}
                onEnded={() => setIsPlaying(false)}
            />

            <div className="flex items-center gap-3">
                {/* Play button */}
                <button
                    onClick={togglePlay}
                    className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center shadow hover:shadow-md transition-shadow"
                >
                    {isPlaying ? (
                        <Pause size={18} className="text-white" />
                    ) : (
                        <Play size={18} className="text-white ml-0.5" />
                    )}
                </button>

                {/* Waveform / Progress */}
                <div className="flex-1 min-w-0">
                    <div
                        className="w-full h-8 bg-white/50 rounded cursor-pointer relative overflow-hidden"
                        onClick={handleProgressClick}
                    >
                        {/* Fake waveform visualization */}
                        <div className="absolute inset-0 flex items-center justify-around px-1">
                            {Array.from({ length: 30 }).map((_, i) => (
                                <div
                                    key={i}
                                    className={`w-0.5 rounded-full transition-colors ${(i / 30) * 100 < progress
                                        ? 'bg-purple-500'
                                        : 'bg-purple-200'
                                        }`}
                                    style={{ height: `${Math.random() * 60 + 20}%` }}
                                />
                            ))}
                        </div>
                    </div>
                    <div className="flex items-center justify-between mt-0.5">
                        <span className="text-[10px] text-slate-500">
                            {formatTime(mediaRef.current?.currentTime || 0)}
                        </span>
                        <span className="text-[10px] text-slate-500">
                            {formatTime(duration)}
                        </span>
                    </div>
                </div>

                {/* Volume */}
                <button
                    onClick={toggleMute}
                    className="p-1.5 hover:bg-white/50 rounded transition-colors"
                >
                    {isMuted ? (
                        <VolumeX size={16} className="text-slate-500" />
                    ) : (
                        <Volume2 size={16} className="text-slate-500" />
                    )}
                </button>
            </div>
        </div>
    );

    /**
     * Render PDF/document card
     */
    const renderDocumentCard = () => (
        <div className="rounded-lg bg-gradient-to-br from-blue-50 to-slate-50 border border-blue-200/60 p-3" style={{ maxWidth: `${mediaWidth}%` }}>
            <div className="flex items-center gap-3">
                {/* Document icon */}
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow">
                    <FileText size={20} className="text-white" />
                </div>

                {/* Document info */}
                <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-slate-700 truncate">
                        {title || fileName || 'Document'}
                    </h4>
                    <p className="text-[10px] text-slate-500">PDF Document</p>
                </div>

                {/* Actions */}
                <a
                    href={src}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
                    title="Open"
                >
                    <ExternalLink size={16} className="text-blue-600" />
                </a>
            </div>
        </div>
    );

    /**
     * Render the appropriate card based on asset type
     */
    const renderCardContent = () => {
        switch (assetType) {
            case 'image':
                return renderImageCard();
            case 'video':
                return renderVideoCard();
            case 'audio':
                return renderAudioCard();
            case 'document':
                return renderDocumentCard();
            default:
                return renderDocumentCard();
        }
    };

    /**
     * Resize handle component - invisible until hover, shows cursor only
     */
    const ResizeHandle = ({ position }) => {
        const cursorClass = {
            'top-left': 'cursor-nwse-resize',
            'top-right': 'cursor-nesw-resize',
            'bottom-left': 'cursor-nesw-resize',
            'bottom-right': 'cursor-nwse-resize',
        }[position] || 'cursor-move';

        const positionClass = {
            'top-left': '-top-2 -left-2',
            'top-right': '-top-2 -right-2',
            'bottom-left': '-bottom-2 -left-2',
            'bottom-right': '-bottom-2 -right-2',
        }[position] || '';

        return (
            <div
                className={`absolute w-4 h-4 ${cursorClass} ${positionClass} z-20`}
                onMouseDown={(e) => handleResizeStart(e, position)}
            />
        );
    };

    return (
        <NodeViewWrapper
            ref={containerRef}
            className={`media-card-wrapper relative my-3 ${selected ? 'ring-2 ring-indigo-300 rounded-lg' : ''}`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            contentEditable={false}
            draggable
            data-drag-handle
        >
            {/* Media container with resize handles */}
            <div className="relative inline-block">
                {/* Card content */}
                {renderCardContent()}

                {/* Resize handles - for images and videos, invisible cursor areas */}
                {(assetType === 'image' || assetType === 'video') && !hasError && !isLoading && (
                    <>
                        <ResizeHandle position="top-left" />
                        <ResizeHandle position="top-right" />
                        <ResizeHandle position="bottom-left" />
                        <ResizeHandle position="bottom-right" />
                    </>
                )}
            </div>

            {/* Caption - editable text field below image */}
            {showCaption && assetType === 'image' && (
                <div className="mt-2" style={{ maxWidth: `${mediaWidth}%` }}>
                    <input
                        type="text"
                        value={caption}
                        onChange={(e) => setCaption(e.target.value)}
                        onBlur={handleCaptionSave}
                        placeholder="Add a caption..."
                        className="w-full text-sm text-slate-500 italic bg-transparent border-none outline-none focus:ring-0 placeholder-slate-300"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.target.blur();
                            }
                        }}
                    />
                </div>
            )}

            {/* Floating action toolbar */}
            <AnimatePresence>
                {isHovered && (
                    <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        className="absolute -top-1 right-0 z-30 flex items-center gap-0.5 bg-white rounded-lg shadow-lg border border-slate-200 p-0.5"
                    >
                        <button
                            onClick={handleDelete}
                            className="p-1.5 hover:bg-red-50 rounded text-slate-400 hover:text-red-500 transition-colors"
                            title="Delete"
                        >
                            <Trash2 size={14} />
                        </button>
                        {assetType === 'image' && (
                            <button
                                onClick={() => setShowCropModal(true)}
                                className="p-1.5 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600 transition-colors"
                                title="Crop"
                            >
                                <Crop size={14} />
                            </button>
                        )}
                        {assetType === 'image' && (
                            <button
                                onClick={() => {
                                    setShowCaption(true);
                                    if (!caption) {
                                        setCaption('');
                                    }
                                }}
                                className="p-1.5 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600 transition-colors"
                                title="Add Caption"
                            >
                                <Type size={14} />
                            </button>
                        )}
                        <div className="w-px h-4 bg-slate-200 mx-0.5" />
                        <div
                            className="p-1.5 text-slate-300 cursor-grab"
                            title="Drag to move"
                            data-drag-handle
                        >
                            <GripVertical size={14} />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Crop Modal */}
            <AnimatePresence>
                {showCropModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                        onClick={() => setShowCropModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-2xl"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 className="text-lg font-semibold text-slate-800 mb-4">Crop Image</h3>

                            {/* Crop preview area */}
                            <div className="relative bg-slate-100 rounded-lg overflow-hidden mb-4" style={{ maxHeight: '400px' }}>
                                <img
                                    src={src}
                                    alt="Crop preview"
                                    className="w-full h-auto"
                                    style={{ maxHeight: '400px', objectFit: 'contain' }}
                                />
                                {/* Crop overlay - simplified version */}
                                <div className="absolute inset-0 border-2 border-dashed border-indigo-400 m-8 rounded pointer-events-none" />
                            </div>

                            <p className="text-sm text-slate-500 mb-4">
                                Drag the corners of the crop area to adjust. This is a simplified crop interface.
                            </p>

                            <div className="flex justify-end gap-2">
                                <button
                                    onClick={() => setShowCropModal(false)}
                                    className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        // TODO: Implement actual crop functionality
                                        // This would typically involve canvas manipulation
                                        // and uploading a new cropped image
                                        setShowCropModal(false);
                                    }}
                                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-500 hover:bg-indigo-600 rounded-lg transition-colors"
                                >
                                    Apply Crop
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </NodeViewWrapper>
    );
};

export default MediaCardNodeView;
